import React, { useEffect, useState } from "react";

import { requestFileUpload } from "_utils/request";

import './FileUpload.sass'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type InvalidStatus<E> = {kind: "invalid", error: E};
type QueuedStatus = {kind: "queued"};
type InProgressStatus = {kind: "inProgress", loaded: number};
type DoneStatus<P> = {kind: "done", result: P};
type ErrorStatus = {kind: "error"};
type FileUploadStatus<P, E> = InvalidStatus<E> | QueuedStatus | InProgressStatus | DoneStatus<P> | ErrorStatus;

type FileUploadState<P, E> = {
  file: File;
  status: FileUploadStatus<P, E>;
}

// Validates a file and turns the validation result into a file upload state.
const newFileState = <P, E>(file: File, validate: (file: File) => Promise<E | null>) => new Promise<FileUploadState<P, E>>(
  resolve => validate(file).then(error => resolve({
    file,
    status: (error
      ? ({kind: "invalid", error} as InvalidStatus<E>)
      : ({kind: "queued"} as QueuedStatus)) as FileUploadStatus<P, E>
  } as FileUploadState<P, E>))
);

// If there was an upload error, another upload is attempted.
function uploadFile<P, E>(fs: FileUploadState<P, E>, setStatus: (status: FileUploadStatus<P, E>) => void, endpoint: string) {
  if (fs.status.kind !== "queued") return;
  setStatus({kind: "inProgress", loaded: 0});

  requestFileUpload<P>(endpoint, fs.file, {
    onUploadProgress: e => setStatus(
      {kind: "inProgress", loaded: Math.round(100 * e.loaded / e.total)}
    )
  }).then(
    result => { setStatus({kind: "done", result}); },
    err => { setStatus({kind: "error"}); }
  );
}

// P: payload, E: error
export function useFileUpload<P, E>(
  validate: (file: File) => Promise<E | null>,
  endpoint: string,
  onDone: (result: P) => void
) {
  const [fileUploadStates, setFileUploadStates] = useState<FileUploadState<P, E>[]>([]);

  // Map the file list to validation promises wrapped into initial file states...
  const addFiles = (files: File[]) => Promise.all(files.map(file => newFileState<P, E>(file, validate)))
    // ...and then append them to our fileUploadStates.
    .then(newFileStates => setFileUploadStates(c => [...c, ...newFileStates]));

  useEffect(() => {
    // Remove finished uploads.
    if (fileUploadStates.some(s => s.status.kind === "done")) {
      fileUploadStates.forEach(f => (f.status.kind === "done") && onDone(f.status.result));
      setFileUploadStates(fs => fs.filter(s => s.status.kind !== "done"));
    }

    // Whenever upload states change, look if there is a file to be uploaded.
    if (fileUploadStates.some(s => s.status.kind === "inProgress")) return;
    const fs = fileUploadStates.find(s => s.status.kind === "queued");
    if (!fs) return;
    const setUploadStatus = (status: FileUploadStatus<P, E>) => setFileUploadStates((states => states.map(s =>
      (s.file === fs.file) ? {...s, status} as FileUploadState<P, E> : s
    )));
    uploadFile(fs, setUploadStatus, endpoint);
  }, [fileUploadStates, endpoint, onDone]);

  const tryAgainFile = (fs: FileUploadState<P, E>) => (fs.status.kind === "error") ? {...fs, status: {kind: "queued"} as QueuedStatus} : fs;
  const tryAgain = () => setFileUploadStates(c => c.map(fs => tryAgainFile(fs)));

  return {fileUploadStates, addFiles, tryAgain};
}

export const FileListEntry = <P, E>({ file, status }: FileUploadState<P, E>) => {
  const progress = (status.kind === "inProgress") ? status.loaded : (status.kind === "done") ? 100 : 0;

  return <div className='file-list-entry'>
    <div className='progress'>
      <div className='progress-bar' style={{width: `${progress}%`}}></div>
    </div>
    {(status.kind === 'invalid') && <span className='badge badge-warning'>{status.error}</span>}
    {(status.kind === 'queued') && <span className='badge badge-info'>QUEUED</span>}
    {(status.kind === 'inProgress') && <span className='badge badge-primary'>IN PROGRESS</span>}
    {(status.kind === 'done') && <span className='badge badge-success'>DONE</span>}
    {(status.kind === 'error') && <span className='badge badge-danger'>ERROR</span>}
    <span>{file.name}</span>
  </div>
}

interface FileUploadProps<P, E> {
  fileUploadStates: FileUploadState<P, E>[];
  tryAgain: () => void;
}

export const FileUpload = <P, E>({ fileUploadStates, tryAgain }: FileUploadProps<P, E>) =>
  <div className='file-upload'>
    <div className='file-list'>
      {fileUploadStates.map((f, index) => <FileListEntry key={index} {...f} />)}
    </div>
    {/*{messages.map(m => <Alert key={m} contextualClass='warning'>{m}</Alert>)}*/}
    {fileUploadStates.some(i => (i.status.kind === "error")) && <button className="btn btn-primary" onClick={tryAgain}>
        <FontAwesomeIcon icon='upload' />&ensp;Try again
    </button>}
    {/*TODO: add a cancel button*/}
  </div>
